import AddressMatch from '@/layouts/services/address_match/AddressMatch.vue';
import AfisCriminalCheck from '@/layouts/services/afis_criminal_check/AfisCriminalCheck.vue';
import AntiMoneyLaundering from '@/layouts/services/anti_money_laundering/AntiMoneyLaundering.vue';
import CompanyVerificationPdf from '@/layouts/services/company_verification_pdf/CompanyVerificationPdf.vue';
import QualificationCheck from '@/layouts/services/qualification_check/QualificationCheck.vue';
import RefugeeVerification from '@/layouts/services/refugee_verification/RefugeeVerification.vue';
import RsaIdVerify from '@/layouts/services/rsa_id_verify/RsaIdVerify.vue';
import CustomTabs from '@/components/CustomTab/CustomTab.vue';
import CustomTable from "@/components/CustomTable/CustomTable.vue";
import InstantServiceCard from '@/components/CustomCard/InstantServiceCard.vue';
import IconCard from "@/components/CustomCard/IconCard.vue";
import SearchBar from '@/components/SearchBar/SearchBar.vue';
import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';
import { LOGOUT } from "@/utils/logout.js";

const STATUS_MAP = {
  Pending: 'pending',
  Approved: 'approved',
  Declined: 'declined'
};

export default {
  name: 'YourServices',
  mixins: [LOGOUT],
  data: () => ({
    approved_requests: [],
    pending_requests: [],
    declined_requests: [],
    all_available_services: [],
    selected_service: null,
    user_role: "",
    searchQuery: "",
    categories: [],
    filteredCategories: [],
    expandedCategories: {},
    loading: true,
    selectedServiceCategory: null,
    availableServiceCategories: [],
    active_services: 0, 
    passed_verifications: 0,
    failed_verifications: 0,
    service_map: {
      anti_money_laundering: AntiMoneyLaundering,
      refugee_verification: RefugeeVerification,
      company_verification_pdf: CompanyVerificationPdf,
      qualification_check: QualificationCheck,
      afis_criminal_check: AfisCriminalCheck,
      rsa_id_verify: RsaIdVerify,
      address_match: AddressMatch
    },
    columns: [
      { label: 'Service Name', key: 'service_name' },
    ]
  }),
  components: {
    AntiMoneyLaundering,
    RefugeeVerification,
    CompanyVerificationPdf,
    QualificationCheck,
    AfisCriminalCheck,
    RsaIdVerify,
    AddressMatch,
    CustomTabs,
    CustomTable,
    InstantServiceCard,
    IconCard,
    SearchBar
  },
  methods: {
    utilize_service(service) {
      this.$router.push({ name: 'utilize_service', params: { service_id : service } });
    },
    close_modal() {
      $('#service_modal').find('input, textarea, select').val('').prop('checked', false);
    },
    async getAllServices() {
      const TOKEN = localStorage.getItem("token");
      try {
        const response = await $.ajax({
          url: `${API_BASE_URL}/verify_api/service_management_api/get_services_api/?instant_verify=true`,
          method: "GET",
          contentType: "application/json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
        });

        if (response.redirect) {
          this.logout();
          return;
        }

        let services = response["services"];
        this.all_available_services = Array.isArray(services) ? services : [services];

        await this.loadAllRequestedServices();
      } catch (error) {
        console.error("Error loading all services:", error);
        this.loading = false;
      }
    },
    async fetchAllPaginatedServices(status) {
      const TOKEN = localStorage.getItem("token");
      let allResults = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        try {
          const response = await $.ajax({
            url: `${API_BASE_URL}/verify_api/workflow_management_api/get_requested_services/?status=${status}&page=${page}`,
            method: "GET",
            dataType: "json",
            headers: {
              Authorization: `Token ${TOKEN}`,
            },
          });

          const results = response.results || [];
          allResults = [...allResults, ...results];

          hasMore = response.next !== null && results.length > 0;
          page++;
        } catch (error) {
          hasMore = false;
        }
      }

      return allResults;
    },
    async loadAllRequestedServices() {
      try {
        const [approved, pending, declined] = await Promise.all([
          this.fetchAllPaginatedServices('approved'),
          this.fetchAllPaginatedServices('pending'),
          this.fetchAllPaginatedServices('declined')
        ]);

        this.approved_requests = approved;
        this.pending_requests = pending;
        this.declined_requests = declined;

        this.organizeServicesIntoCategories();
      } catch (error) {
        console.error("Error loading requested services:", error);
      } finally {
        this.loading = false;
      }
    },
    organizeServicesIntoCategories() {
      const activeApproved = this.approved_requests;

      const requestedServiceIds = new Set([
        ...this.approved_requests.map(s => s.service_id || s.id),
        ...this.pending_requests.map(s => s.service_id || s.id),
        ...this.declined_requests.map(s => s.service_id || s.id)
      ]);

      // Filter out services that are not in any request status (Inactive Services)
      const inactiveServices = this.all_available_services
        .filter(service => {
          // Exclude services that are already in approved/pending/declined
          if (requestedServiceIds.has(service.id)) return false;
          
          // Exclude custom forms (services without service_details)
          if (!service.service_details || service.service_details.length === 0) return false;
          return true;
        })
        .map(service => ({
          service_id: service.id,
          service_name: service.service_name,
          name: null,
          service_category: service.service_details?.[0]?.service_categories || null,
          isActive: false
        }));

      // Flatten services for each category
      const approvedCategory = {
        id: 1,
        name: "Active",
        services: activeApproved.map(service => ({ ...service, isActive: true }))
      };

      const pendingCategory = {
        id: 2,
        name: "Pending",
        services: this.pending_requests.map(service => ({ ...service, isActive: false }))
      };

      const declinedCategory = {
        id: 3,
        name: "Declined",
        services: this.declined_requests.map(service => ({ ...service, isActive: false }))
      };

      const inactiveCategory = {
        id: 4,
        name: "Inactive",
        services: inactiveServices
      };

      const allServices = [
        ...activeApproved,
        ...this.pending_requests,
        ...this.declined_requests,
        ...inactiveServices
      ];

      const categorySet = new Set();
      allServices.forEach(service => {
        if (service.service_category?.category_name) {
          categorySet.add(service.service_category.category_name);
        }
      });

      this.availableServiceCategories = Array.from(categorySet).map(cat => ({
        id: cat,
        name: this.formatCategoryName(cat)
      }));

      this.categories = [approvedCategory, pendingCategory, declinedCategory, inactiveCategory]
        .filter(category => category.services && category.services.length > 0);

      this.applyFilters();

      this.expandedCategories = {
        1: true,
        2: true,
        3: true,
        4: true
      };
    },
    formatCategoryName(categoryName) {
      // Shortened category names
      const shortNames = {
        'personnel_verification': 'Person',
        'company_verification': 'Company',
        'compliance_verification': 'Compliance',
        'credit_verification': 'Credit'
      };
      return shortNames[categoryName] || categoryName
        .replace(/_verification/g, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    },
    selectServiceCategory(categoryId) {
      this.selectedServiceCategory = this.selectedServiceCategory === categoryId ? null : categoryId;
      this.applyFilters();
    },
    applyFilters() {
      let filtered = [...this.categories];

      if (this.selectedServiceCategory) {
        filtered = filtered.map(category => {
          // Only filter Inactive Services by category
          if (category.id === 4) {
            return {
              ...category,
              services: category.services.filter(service => 
                service.service_category?.category_name === this.selectedServiceCategory
              )
            };
          }
          // Keep other categories as-is
          return category;
        }).filter(category => category.services.length > 0);
      }

      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.map(category => ({
          ...category,
          services: category.services.filter(service =>
            service.service_name.toLowerCase().includes(query)
          )
        })).filter(category => category.services.length > 0);
      }

      this.filteredCategories = filtered;
    },
    filterServices() {
      this.applyFilters();
    },
    clearSearch() {
      this.searchQuery = '';
      this.applyFilters();
    },
    toggleCategory(categoryId) {
      this.expandedCategories[categoryId] = !this.expandedCategories[categoryId];
    },
    isCategoryExpanded(categoryId) {
      return this.expandedCategories[categoryId] || false;
    },
    getDisplayedServices(category) {
      const initialLimit = 8;
      if (this.isCategoryExpanded(category.id) || this.searchQuery || this.selectedServiceCategory) {
        return category.services;
      }
      return category.services.slice(0, initialLimit);
    },
    shouldShowMoreButton(category) {
      return category.services.length > 8 && !this.searchQuery && !this.selectedServiceCategory;
    },
    handleServiceRequest(data) {
      const service = data.service;
      const isSelected = data.requested;

      const serviceId = service.service_id || service.id;

      // Send API request
      this.send_service_request(serviceId, isSelected);

      if (isSelected) {
        // Service is being enabled - check declined first, then inactive
        const declinedIndex = this.declined_requests.findIndex(
          s => s.service_id === serviceId
        );
        
        if (declinedIndex !== -1) {
          const movedService = this.declined_requests.splice(declinedIndex, 1)[0];
          this.pending_requests.push(movedService);
          this.organizeServicesIntoCategories();
          return;
        }

        // Check if it's an inactive service (from all_available_services)
        const inactiveService = this.all_available_services.find(
          s => s.id === serviceId
        );
        
        if (inactiveService) {
          // Create a pending request entry for this inactive service
          const pendingEntry = {
            service_id: inactiveService.id,
            service_name: inactiveService.service_name,
            service_category: inactiveService.service_details?.[0]?.service_categories || null,
            isActive: false
          };
          this.pending_requests.push(pendingEntry);
          this.organizeServicesIntoCategories();
        }
      } else {
        // Service is being disabled - move from approved to pending (for deactivation)
        const approvedIndex = this.approved_requests.findIndex(
          s => s.service_id === serviceId
        );
        
        if (approvedIndex !== -1) {
          const movedService = this.approved_requests.splice(approvedIndex, 1)[0];
          this.pending_requests.push(movedService);
          this.organizeServicesIntoCategories();
        }
      }
    },
    send_service_request(service_id, is_selected) {
      const TOKEN = localStorage.getItem("token");
      const SELECT_SERVICES_API = `${API_BASE_URL}/verify_api/workflow_management_api/instant_verify_request_api/`;

      const FORM_DATA = {
        service_id: service_id,
        is_selected: is_selected,
      };

      $.ajax({
        url: SELECT_SERVICES_API,
        method: "POST",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        data: JSON.stringify(FORM_DATA),
        success: () => {},
        error: (error) => {
          console.error("Error sending service request:", error);
        },
      });
    },
    get_services_requested(page = 1, search = "") {
      const TOKEN = localStorage.getItem("token");
      const status = STATUS_MAP[this.currentTab];
      return new Promise((resolve, reject) => {
        let url = `${API_BASE_URL}/verify_api/workflow_management_api/get_requested_services/?page=${page}&status=${status}`;
        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }
        $.ajax({
          url: url,
          method: "GET",
          dataType: "json",
          headers: {
            Authorization: `Token ${TOKEN}`,
          },
          success: function (data) {
            resolve({
              count: data.count,
              results: data.results,
            });
          },
          error: function (xhr, status, error) {
            reject(error);
          },
        });
      });
    },
    view_service(name) {
      if (this.service_map[name]) {
        this.selected_service = name;
        $('#service_modal').modal('show');
      } else {
        console.error("Service not found");
      }
    },
    view_logs(service_id) {
      this.$router.push({ name: 'verify_logs' });
      this.$store.commit('set_service_id', service_id);
    },
    get_instant_verify_dashboard_data() {
      const TOKEN = localStorage.getItem('token');
      const GET_DASHBOARD_DATA_API = `${API_BASE_URL}/verify_api/workflow_management_api/instant_verify_dashboard/`;

      $.ajax({
        url: GET_DASHBOARD_DATA_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        success: (response) => {
          if (response.redirect){
            this.logout();
            return;
          }
          this.active_services = response.active_services || 0;
          this.passed_verifications = response.successful_services || 0;
          this.failed_verifications = response.failed_services || 0;
        },
        error: (xhr, status, error) => {
          console.error('Error loading dashboard data:', error);
        }
      });
    }
  },
  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }
    this.user_role = this.$store.getters.get_user_role;
    this.getAllServices();
    this.get_instant_verify_dashboard_data();
  }
};
